import { useTheme } from 'next-themes';
import { Settings } from 'src/components/Settings/SettingsForm';
import { NOTIFICATION_CONFIGURATIONS } from 'src/constants/constants';
import { VelocityTheme } from 'src/environmentVariables/EnvironmentVariableTypes';
import { Updater } from 'use-immer';
import Image from 'next/image';
import { cj } from 'src/utils/classNames';
import { WithChildren } from 'src/utils/UtilityTypes';
import {
	TRADE_PAGE_GRID_CONFIGURATIONS,
	TRADE_PAGE_GRID_CONFIGURATIONS_NO_ORDERBOOK,
} from 'src/hooks/useTradePageLayout/type';
import useCurrentSettings from 'src/hooks/useCurrentSettings';

interface Props {
	localSettings: Settings;
	setLocalSettings: Updater<Settings>;
}

const IMAGE_WIDTH = 130;
const IMAGE_HEIGHT = 75;

const NO_OB_LAYOUT_HIGHLIGHT_MAP: Record<string, string> = {
	split: 'default',
	splitMirrored: 'mirrored',
};

/**
 * Contains settings for trade components layout, and notification placement.
 * Only rendered on desktop, as these settings don't apply to mobile.
 */
export default function LayoutSettings({
	localSettings,
	setLocalSettings,
}: Props) {
	const currentTheme = useTheme().theme ?? VelocityTheme.dark;
	const [savedSettings] = useCurrentSettings();
	const showOrderbook = savedSettings.showOrderbook !== false;

	const { layoutSetting2, notificationPlacement } = localSettings;

	const layoutConfigurations = showOrderbook
		? TRADE_PAGE_GRID_CONFIGURATIONS
		: TRADE_PAGE_GRID_CONFIGURATIONS_NO_ORDERBOOK;

	const highlightedLayout = showOrderbook
		? layoutSetting2
		: (NO_OB_LAYOUT_HIGHLIGHT_MAP[layoutSetting2] ?? layoutSetting2);

	return (
		<div className="flex flex-col gap-y-2xl text-text-default">
			<div className="flex flex-col gap-y-sm">
				<h2 className="typo-label-sm">Trade Layout</h2>
				<div className="grid grid-cols-4 gap-md">
					{layoutConfigurations.map((config) => (
						<ImageButton
							key={config.value}
							isSelected={highlightedLayout === config.value}
							onClick={() =>
								setLocalSettings({
									...localSettings,
									layoutSetting2: config.value,
								})
							}
						>
							<Image
								width={IMAGE_WIDTH}
								height={IMAGE_HEIGHT}
								className="h-auto w-full"
								alt={config.label}
								src={
									showOrderbook
										? `/assets/icons/buttonIcons/layout/${config.value}-${currentTheme}-1.svg`
										: `/assets/icons/buttonIcons/layout/${config.value}-${currentTheme}-2.svg`
								}
							/>
						</ImageButton>
					))}
				</div>
			</div>
			<div className="flex flex-col gap-y-sm">
				<h2 className="typo-label-sm">Notification Placement</h2>
				{/* `grid-cols-4` so the image widths align with the trade layout images above. */}
				<div className="grid grid-cols-4 gap-md">
					{NOTIFICATION_CONFIGURATIONS.map((config) => (
						<ImageButton
							key={config.value}
							isSelected={notificationPlacement === config.value}
							onClick={() =>
								setLocalSettings({
									...localSettings,
									notificationPlacement: config.value,
								})
							}
						>
							<Image
								width={IMAGE_WIDTH}
								height={IMAGE_HEIGHT}
								alt={config.label}
								src={`/assets/icons/buttonIcons/notificationPlacement/${config.value}-${currentTheme}.svg`}
							/>
						</ImageButton>
					))}
				</div>
			</div>
		</div>
	);
}

interface ImageButtonProps extends WithChildren {
	isSelected: boolean;
	onClick: () => void;
}

function ImageButton({ isSelected, onClick, children }: ImageButtonProps) {
	return (
		<button
			className={cj(
				'overflow-hidden rounded-[6px] border-2 hover:border-button-primary-bg-hover',
				isSelected ? 'border-button-primary-bg' : 'border-stroke-divider'
			)}
			onClick={onClick}
			data-state={isSelected ? 'checked' : 'unchecked'}
		>
			{children}
		</button>
	);
}
